import PropTypes from 'prop-types';
import { Box, Typography, alpha, useTheme } from '@mui/material';

/**
 * SandboxedHtmlPreview — tiny shared wrapper around the iframe srcdoc render
 * pattern already proven in src/sections/@dashboard/campaign-builder/steps/
 * TemplatePreviewPanel.jsx (lines 107-117).
 *
 * Why a separate component:
 *   - Step 4 of the Send Forms dialog and the campaign-builder preview were
 *     drifting on what "safe HTML preview" means. The campaign-builder one is
 *     correct (sandboxed iframe, no scripts); Step 4 was rendering raw HTML
 *     inside a <Typography>, which leaked markup as literal text and lacked
 *     iframe isolation.
 *   - Centralising the iframe means every consumer inherits the same security
 *     posture: `sandbox="allow-same-origin"` deliberately omits
 *     `allow-scripts` so even if a template author embeds <script>, it can't
 *     execute. Defence in depth — the BE only renders patient PII, never JS,
 *     but we belt-and-braces the FE too.
 *
 * Props are intentionally narrow. Callers compose their own headers/chrome
 * around this component; this is just the body renderer.
 */
const SandboxedHtmlPreview = ({ html, height, ariaLabel }) => {
    const theme = useTheme();

    // Empty / null body → small dashed-border placeholder. Mirrors the
    // "Pick a template" placeholder copy convention used elsewhere in the
    // dialog so the empty state is visually consistent.
    if (!html) {
        return (
            <Box
                sx={{
                    height,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: '8px',
                    border: '1px dashed',
                    borderColor: alpha(theme.palette.grey[500], 0.32),
                    bgcolor: alpha(theme.palette.grey[500], 0.04),
                }}
            >
                <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '11.5px' }}>
                    Nothing to preview
                </Typography>
            </Box>
        );
    }

    // Wrap the BE-rendered body in real-email chrome — centered white card
    // on a light gray "inbox" background, system font, sensible line-height,
    // links underlined in standard email-client blue. Without this, the BE
    // returns bare HTML that renders as left-aligned unstyled text and looks
    // broken compared to what the recipient sees in Mailgun/Gmail/Outlook.
    //
    // ``<base target="_blank">`` + sandbox ``allow-popups`` ``allow-popups-to-escape-sandbox``
    // so clicking the form link opens the real URL in a new tab. Without
    // these the browser silently blocks navigation from a sandboxed frame.
    // We still omit ``allow-scripts`` — defence in depth against template
    // bodies that contain a <script> tag.
    const wrapped = `<!doctype html><html><head><meta charset="utf-8"><base target="_blank"><style>
      html,body{margin:0;padding:0;background:#F4F6F8;-webkit-font-smoothing:antialiased;}
      body{font:14px/1.55 -apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,Oxygen,Ubuntu,sans-serif;color:#1C252E;}
      .__email-card{max-width:560px;margin:18px auto;background:#fff;border-radius:10px;
        box-shadow:0 1px 2px rgba(145,158,171,0.16),0 1px 0 rgba(145,158,171,0.08);
        padding:24px 28px;}
      .__email-card a{color:#0B79D0;text-decoration:underline;word-break:break-word;}
      .__email-card p{margin:0 0 10px;}
      .__email-card img{max-width:100%;height:auto;}
      .__email-card table{max-width:100%;}
    </style></head><body><div class="__email-card">${html}</div></body></html>`;

    return (
        <iframe
            title={ariaLabel || 'Email preview'}
            srcDoc={wrapped}
            sandbox="allow-same-origin allow-popups allow-popups-to-escape-sandbox"
            style={{
                width: '100%',
                height,
                border: 0,
                background: '#F4F6F8',
                borderRadius: 8,
            }}
        />
    );
};

SandboxedHtmlPreview.propTypes = {
    html: PropTypes.string,
    height: PropTypes.number,
    ariaLabel: PropTypes.string,
};

SandboxedHtmlPreview.defaultProps = {
    html: '',
    height: 240,
    ariaLabel: 'Email preview',
};

export default SandboxedHtmlPreview;
